import React, { useState } from "react";
import {
  Autocomplete,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  Checkbox,
  Box,
} from "@mui/material";
import { ExpandLess, ExpandMore, Close } from "@mui/icons-material";
import { getItemApi } from "../../services/item.service";
import { useGetApi } from "../../hooks/useGetApi";

const ServiceRow = ({
  service,
  index,
  handleServicesChange,
  categoryList,
  formData,
  doctorsList,
  removeServicesDetail,
  selectedServices,
  toggleRowSelection,
  consultationType = "OPD",
}) => {
  const { dataList: itemList } = useGetApi({
    apiFunction: service?.category?._id ? getItemApi : null,
    params: {
      category: service?.category?._id,
      limit: Number.MAX_SAFE_INTEGER,
    },
    dataKey: "items",
    dependencies: [service?.category?._id],
  });

  const [isWaiverSelected, setIsWaiverSelected] = useState(false);

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {/* Category Dropdown */}
      <Grid item xs={12} sm={6} md={4} xl={3}>
        <Grid container spacing={1} alignItems="center">
          {formData?.services?.length > 1 && (
            <Grid item xs={2}>
              <Checkbox
                checked={selectedServices?.includes(
                  service?._id || service?.id
                )}
                onChange={() => toggleRowSelection(service?._id || service?.id)}
              />
            </Grid>
          )}
          <Grid item xs={formData?.services?.length > 1 ? 10 : 12}>
            <Autocomplete
              options={categoryList || []}
              getOptionLabel={(option) => option?.categoryName || ""}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Category" 
                  required={consultationType !== "IPD"}
                  InputLabelProps={{
                    required: consultationType !== "IPD",
                    sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                  }}
                />
              )}
              value={service?.category || null}
              onChange={(_, newValue) =>
                handleServicesChange(
                  { target: { name: "category", value: newValue } },
                  index
                )
              }
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Item Dropdown */}
      <Grid item xs={12} sm={6} md={4} xl>
        <Autocomplete
          options={itemList || []} // Row-specific itemList
          getOptionLabel={(option) => option?.itemName || ""}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Item Name" 
              required
              InputLabelProps={{
                required: true,
                sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
              }}
            />
          )}
          value={service?.item || null}
          onChange={(_, newValue) =>
            handleServicesChange(
              { target: { name: "item", value: newValue } },
              index
            )
          }
          disabled={!service.category} // Disable if no category selected
        />
      </Grid>

      {/* Quantity Input
      <Grid item xs={12} sm={6} md={4} xl>
        <TextField
          type="number"
          label="Quantity"
          name="quantity"
          value={service?.quantity || ""}
          onChange={(e) => handleServicesChange(e, index)}
          fullWidth
        />
      </Grid> */}

      {/* Assigned Doctor Dropdown */}
      <Grid item xs={12} sm={6} md={4} xl>
        <Autocomplete
          options={doctorsList || []}
          getOptionLabel={(option) =>
            `${option?.firstName || ""} ${option?.lastName || ""} (${
              option?.userId || ""
            })`
          }
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Assigned Doctor" 
              required
              InputLabelProps={{
                required: true,
                sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
              }}
            />
          )}
          value={service?.assignedDoctor || null}
          onChange={(_, newValue) =>
            handleServicesChange(
              { target: { name: "assignedDoctor", value: newValue } },
              index
            )
          }
        />
      </Grid>

      {/* Amount Input */}
      <Grid item xs={12} sm={6} md={4} xl>
        <TextField
          type="number"
          label="Amount"
          name="amount"
          value={service?.amount || service?.item?.normalRate || ""}
          onChange={(e) => handleServicesChange(e, index)}
          fullWidth
        />
      </Grid>

      {/* Waiver Toggle and Remove Button */}
      <Grid item xs={12} sm={6} md={4} xl>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              value="Waiver"
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {isWaiverSelected ? <ExpandLess /> : <ExpandMore />}
                    </InputAdornment>
                  ),
                  sx: { cursor: "pointer" },
                },
              }}
              onClick={() => setIsWaiverSelected((prev) => !prev)}
              sx={{ cursor: "pointer" }}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Referred By Doctor and Delete Button */}
      <Grid item xs={12} sm={6} md={4} xl>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label="Referred By Doctor"
            name="referringDoctorName"
            value={service?.referringDoctorName || ""}
            onChange={(e) => handleServicesChange(e, index)}
            fullWidth
          />
          {formData?.services?.length > 1 && (
            <IconButton
              color="error"
              onClick={() => removeServicesDetail(service?._id || service?.id)}
              sx={{ ml: 1 }}
            >
              <Close />
            </IconButton>
          )}
        </Box>
      </Grid>

      {/* Waiver Details (if selected) */}
      {isWaiverSelected && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Waiver Reason"
                name="waiverReason"
                value={service?.waiver?.waiverReason || ""}
                onChange={(e) => handleServicesChange(e, index, "waiver")}
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Approved By"
                name="approvedBy"
                value={service?.waiver?.approvedBy || ""}
                onChange={(e) => handleServicesChange(e, index, "waiver")}
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={["Percentage", "Amount"]}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Waiver Type" fullWidth />
                )}
                value={service?.waiver?.waiverType || null}
                onChange={(_, newValue) =>
                  handleServicesChange(
                    { target: { name: "waiverType", value: newValue } },
                    index,
                    "waiver"
                  )
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                type="number"
                label={
                  service?.waiver?.waiverType === "Percentage"
                    ? "Percentage"
                    : "Amount"
                }
                name={
                  service?.waiver?.waiverType === "Percentage"
                    ? "waiverPercentage"
                    : "waiverAmount"
                }
                value={
                  service?.waiver?.waiverType === "Percentage"
                    ? service?.waiver?.waiverPercentage || ""
                    : service?.waiver?.waiverAmount || ""
                }
                onChange={(e) => handleServicesChange(e, index, "waiver")}
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default ServiceRow;
